import { ReactNode } from "react";
import { Badge, Box } from "@chakra-ui/react";
import Link, { LinkProps } from "components/Shared/Link";
import { Url } from "utils/links";
import {
  DocumentType,
  FragmentType,
  getFragmentData,
  gql,
} from "__generated__/gql";
import NameWithSuffix from "components/Shared/RetailerDirectory/NameWithSuffix";

const fragment = gql(/* GraphQL */ `
  fragment CommentRelatedItem on Comment {
    id
    brandRetailerComment {
      commentId
      brandRetailer {
        id
        retailer {
          id
          ...RetailerNameWithSuffixFragment
        }
      }
    }
  }
`);

type RelatedItemLinkProps = Omit<LinkProps, "children"> & {
  relatedName: ReactNode;
  relatedType: ReactNode;
};

function RelatedItemLink({
  relatedName,
  relatedType,
  ...rest
}: RelatedItemLinkProps) {
  return (
    <Badge
      as={rest.href ? Link : undefined}
      variant="outline"
      colorScheme="lightPinkButton"
      isTruncated
      minW={0}
      fontWeight={500}
      flex="0 1 auto"
      textTransform="none"
      {...rest}
    >
      <Box as="span" fontWeight={300}>
        {relatedType}:
      </Box>{" "}
      {relatedName}
    </Badge>
  );
}

type Props = Omit<RelatedItemLinkProps, "relatedName" | "relatedType"> & {
  data: FragmentType<typeof fragment>;
  getLink?: (comment: DocumentType<typeof fragment>) => Url | undefined;
};

export default function CommentRelatedItem({ data, getLink, ...rest }: Props) {
  const comment = getFragmentData(fragment, data);

  if (comment.brandRetailerComment) {
    const bcr = comment.brandRetailerComment.brandRetailer;
    const cr = bcr?.retailer;
    if (!bcr || !cr) return null;
    return (
      <RelatedItemLink
        href={getLink?.(comment)}
        relatedName={<NameWithSuffix retailer={cr} />}
        relatedType="Retailer"
        {...rest}
      />
    );
  }

  return null;
}
